import {
  FunctionComponent,
  useEffect,
  useCallback,
  useRef,
  useState,
} from 'react'
import { Grid } from '@material-ui/core'
import { MdCheck } from 'react-icons/md'
import { useHistory } from 'react-router'
import { useLocation } from 'react-router-dom'
import { FormikProps, FormikValues } from 'formik'
import * as Yup from 'yup'

import ContainerPage from 'shared/components/ContainerPageNew'
import RadioButton from 'shared/components/Radio'
import Form from 'shared/components/Form'
import Modal from 'shared/components/Modal'
import Text from 'shared/components/Text'
import Button from 'shared/components/Button'
import SnackBar from 'shared/components/SnackBar'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import Switch from 'shared/components/Switch'

import userProfile from 'shared/config/userProfile'
import { cnpjMask, phoneMask } from 'shared/utils/formatters'
import { colors } from 'shared/styles/colors'

import { ISignIn } from 'modules/auth/domain/usecases/SignInImpl'
import IDadosUsuarioResponse from 'modules/user/domain/entities/DadosUsuario'
import { IUpdateUsuario } from 'modules/user/domain/usecases/UpdateUsuarioImpl'
import { useFrotista } from 'modules/user/hooks/useFrotista'

import ModalSenha from './ModalSenha'
import TransferList from './TransferList'
import { StyledCheck, Title, StyledInput, WrapperRadio } from './styles'
export interface IProps {
  signIn: ISignIn
  updateUser: IUpdateUsuario
}

export interface ICnpjFrotista {
  cnpj: string
  id: number
}

type TLocation = {
  params: IDadosUsuarioResponse
  isEditable: boolean
}

const EditUsuario: FunctionComponent<IProps> = ({ signIn, updateUser }) => {
  const location = useLocation<TLocation>()
  const { frotistas } = useFrotista()
  const [usuario] = useState(location.state?.params)
  const [isEditable] = useState(location.state?.isEditable)
  const [isModifyUser, setIsModifyUser] = useState(false)
  const cnpjsSelecionados = ''
  const [accountType, setAccountType] = useState(usuario.perfil)
  const [activeUser, setActiveUser] = useState(usuario.ativo)
  const formRef = useRef<FormikProps<FormikValues>>()
  const history = useHistory()
  const [showModalSucesso, setShowModalSucesso] = useState(false)
  const [availableCnpjs, setAvailableCnpjs] = useState<ICnpjFrotista[]>([])
  const [searchAvailableCnpjs, setSearchAvailableCnpjs] = useState<
    ICnpjFrotista[]
  >([])
  const [selectedCnpjs, setSelectedCnpjs] = useState<ICnpjFrotista[]>([])
  const [initialSelectedCnpjs, setInitialSelectedCnpjs] = useState<
    ICnpjFrotista[]
  >([])
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)
  const [openModalPassword, setOpenModalPassword] = useState(false)

  useEffect(() => {
    const cnpjs: ICnpjFrotista[] = []
    frotistas.forEach(frotista => {
      const haveFrotistaInUser = usuario.frotistas.some(
        userFrot => userFrot.id === frotista.id,
      )
      if (!haveFrotistaInUser)
        cnpjs.push({ cnpj: cnpjMask(frotista.cnpj), id: frotista.id })
    })
    setAvailableCnpjs(cnpjs)
    setSearchAvailableCnpjs(cnpjs)

    const userCnpjs = usuario.frotistas.map(userFrotista => {
      return {
        id: userFrotista.id,
        cnpj: cnpjMask(userFrotista.cnpj),
      }
    })

    setSelectedCnpjs(userCnpjs)
    setInitialSelectedCnpjs(userCnpjs)
  }, [frotistas, usuario])

  const handleCloseModal = useCallback(() => {
    setActiveUser(state => !state)
  }, [])

  const handleOpenModalPassword = useCallback(() => {
    setOpenModalPassword(true)
  }, [])

  const handleSubmitOk = useCallback(async () => {
    try {
      setLoadingBackdrop(true)
      const frotistas_ids = selectedCnpjs.map(frotista => {
        return frotista.id
      })

      await updateUser.execute(usuario.id, {
        ativo: activeUser,
        frotistas_ids,
        perfil: accountType,
      })
      setLoadingBackdrop(false)
    } catch (error: unknown) {
      setLoadingBackdrop(false)
      if (error instanceof Error) {
        setErrorMessage(error.message)
      } else {
        setErrorMessage('Erro ao tentar atualizar o usuário')
      }
      setShowError(true)
      setActiveUser(!activeUser)
    }
  }, [activeUser, updateUser, usuario, selectedCnpjs, accountType])

  const handleSubmit = useCallback(async () => {
    handleOpenModalPassword()
  }, [handleOpenModalPassword])

  const handleChangeStatus = useCallback(() => {
    handleOpenModalPassword()
    setActiveUser(state => !state)
  }, [handleOpenModalPassword])

  const handleAccountType = useCallback((type: string) => {
    setAccountType(type)
    setIsModifyUser(true)
  }, [])

  const handleSetSelectedCnpjs = useCallback((param: ICnpjFrotista[]) => {
    setSelectedCnpjs(param)
    setIsModifyUser(true)
  }, [])

  return (
    <>
      {openModalPassword && (
        <ModalSenha
          setOpenModalPassword={setOpenModalPassword}
          openModalPassword={openModalPassword}
          signIn={signIn}
          handleExecuteCallback={handleSubmitOk}
          handleCloseModal={handleCloseModal}
        />
      )}

      <ContainerPage
        breadCrumb="Home / Gerenciar Conta / Usuários / Editar usuário"
        titleNextButton="Salvar"
        onClickNextButton={isEditable ? handleSubmit : null}
        isNextButtonDisabled={!isModifyUser}
        titleBackButton="Voltar"
        onClickBackButton={() => history.goBack()}
        iconButtonNext={MdCheck}
        title="Dados pessoais"
        titleHeader="Editar Usuário"
      >
        <Form
          validationSchema={() => ({
            accountType: Yup.string().required(),
            cnpjsSelecionados: Yup.string().required(),
          })}
          onSubmit={undefined}
          innerRef={formRef}
          initialValues={{
            nome: usuario.nome || '',
            email: usuario.login || '',
            telefone: usuario.telefone || '',
            accountType: usuario.perfil || '',
            cnpjsSelecionados,
          }}
        >
          {formControl => (
            <Grid container spacing={3}>
              <Grid item xs={3}>
                <StyledInput
                  label="Nome"
                  name="nome"
                  type="text"
                  formControl={formControl}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <StyledInput
                  label="Email"
                  name="email"
                  type="text"
                  formControl={formControl}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <StyledInput
                  label="Telefone celular"
                  name="telefone"
                  type="text"
                  maxLength={15}
                  mask={value => phoneMask(value)}
                  formControl={formControl}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <Switch
                  id="status"
                  checked={activeUser}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onChange={isEditable ? handleChangeStatus : () => {}}
                />
              </Grid>
              <Grid item xs={12} style={{ marginTop: -20 }}>
                <Title>Tipo de conta:</Title>
              </Grid>

              <Grid
                container
                spacing={3}
                style={{ margin: '-40px 0px 0px 0px' }}
              >
                <Grid item xs={4}>
                  <WrapperRadio>
                    <Grid xs={12}>
                      <RadioButton
                        id="OPERACIONAL"
                        label="Operacional"
                        name="accountType"
                        value="OPERACIONAL"
                        onChange={
                          isEditable
                            ? activeUser
                              ? handleAccountType
                              : // eslint-disable-next-line @typescript-eslint/no-empty-function
                                () => {}
                            : // eslint-disable-next-line @typescript-eslint/no-empty-function
                              () => {}
                        }
                        checked={accountType === userProfile.status.operacional}
                      />
                      <span style={{ marginTop: 20, fontSize: 12 }}>
                        Essa conta não pode:
                      </span>
                      <ul
                        style={{
                          fontSize: 12,
                          color: colors.graylightest,
                          marginLeft: 20,
                          marginTop: 10,
                        }}
                      >
                        <li>Acessar o menu de Pagamentos</li>
                        <li>
                          Criar, editar ou desativar o cadastro de outros
                          usuários
                        </li>
                        <li>Criar, editar e dasativar CNPJs</li>
                      </ul>
                    </Grid>
                  </WrapperRadio>
                </Grid>

                <Grid item xs={4}>
                  <WrapperRadio>
                    <Grid xs={12}>
                      <RadioButton
                        id="ADMIN"
                        label="Administrador"
                        name="accountType"
                        value="ADMIN"
                        onChange={
                          isEditable
                            ? activeUser
                              ? handleAccountType
                              : // eslint-disable-next-line @typescript-eslint/no-empty-function
                                () => {}
                            : // eslint-disable-next-line @typescript-eslint/no-empty-function
                              () => {}
                        }
                        checked={accountType === userProfile.status.admin}
                      />
                      <span style={{ marginTop: 20, fontSize: 12 }}>
                        Essa conta não pode:
                      </span>
                      <ul
                        style={{
                          fontSize: 12,
                          color: colors.graylightest,
                          marginLeft: 20,
                          marginTop: 10,
                        }}
                      >
                        <li>
                          Editar e dasativar perfis de Administradores e Máster
                        </li>
                      </ul>
                    </Grid>
                  </WrapperRadio>
                </Grid>
              </Grid>

              <Grid container style={{ margin: '0px 10px' }}>
                <Title>Vincular CNPJ:</Title>
              </Grid>
              <TransferList
                availableCnpjs={availableCnpjs}
                setAvailableCnpjs={setAvailableCnpjs}
                selectedCnpjs={selectedCnpjs}
                setSelectedCnpjs={handleSetSelectedCnpjs}
                searchAvailableCnpjs={searchAvailableCnpjs}
                setSearchAvailableCnpjs={setSearchAvailableCnpjs}
                initialSelectedCnpjs={initialSelectedCnpjs}
                isEditable={isEditable && activeUser}
              />
            </Grid>
          )}
        </Form>

        {showModalSucesso && (
          <Modal
            openModal={showModalSucesso}
            size="small"
            showCloseButton={false}
            onCloseModal={() => setShowModalSucesso(false)}
          >
            <StyledCheck style={{ marginBottom: 20, marginTop: 40 }} />
            <Text
              style={{ fontSize: 20, marginBottom: 20, fontWeight: 'bold' }}
              color={colors.blue}
            >
              Cadastro realizado com sucesso
            </Text>

            <Text style={{ margin: '20px' }}>
              O usuário cadastrado receberá um email para acessar o seu perfil.
            </Text>
            <Button
              color={colors.green}
              onClick={() => {
                history.push('/usuarios-vinculados')
                setShowModalSucesso(false)
              }}
            >
              <MdCheck />
              Ok
            </Button>
          </Modal>
        )}

        <SnackBar
          open={showError}
          setOpen={setShowError}
          severity="error"
          message={errorMessage}
          horizontal="center"
        />

        <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
      </ContainerPage>
    </>
  )
}

export default EditUsuario
